import { OABHeader, OABLineItem } from '../types/oab';

export interface CreateOABPayload {
  folio: string;
  fechaEmision: string;
  tasaBCV: number;
  totalUSD: number;
  totalBs: number;
  notas?: string;
  lineas: OABLineItem[];
}

export interface ReceptionItemPayload {
  solicitudId?: string;
  dashboardId?: string;
  insumoId?: string;
  codigo?: string;
  unidadMedida?: string;
  nombre: string;
  cantidadAprobada: number;
  cantidadRecibida: number;
  cantidadRecibidaHoy?: number;
  cantidadRecibidaPrevia?: number;
  backorderPendiente?: number;
  cantidadRechazada: number;
  costoUnitarioUSD: number;
  costoAprobadoUSD?: number;
  toleranciaExcedente?: boolean;
  notasDiscrepancia?: string;
  pedidoId?: string;
  proyectoId?: string;
  proyectoNombre?: string;
  originalPedidoId?: string;
  originalProyectoNombre?: string;
  parentSolicitudId?: string;
  originalCantidadAprobada?: number;
  isSplitChild?: boolean;
  bultos?: number;
  cantEnBulto?: number;
  bultoPico?: number;
  desgloseBultos?: number[];
  actionOnRemaining?: 'BACKORDER' | 'DISCARD' | 'REQUOTE_ANOTHER_VENDOR';
  motivoSaldo?: string;
  rutaLogistica?: 'VIA_GALPON' | 'DIRECTO_OBRA' | 'SERVICIO_SITIO' | 'NO_APLICA';
  orderLineId?: string | null;
  isDirectOrService?: boolean;
}

export interface RegisterReceptionPayload {
  folioOAB: string;
  parentFolioOAB?: string;
  oabId?: string;
  proveedorId?: string;
  proveedor?: string;
  proveedorNombre?: string;
  numeroNotaEntrega: string;
  fechaRecepcion?: string;
  tasaBCV?: number;
  items: ReceptionItemPayload[];
  comprobanteUrl?: string;
  comprobanteFile?: string; // Base64 temporal en caso de captura offline
  numeroFacturaFiscal?: string; // SENIAT Factura Legal (Opcional)
  numeroControlFiscal?: string; // SENIAT Número de Control (Opcional)
  fotoPendienteSync?: boolean; // Bandera de resiliencia R2
  tipoDocumento?: 'SENIAT' | 'NOTA_ENTREGA' | 'FLETE_INTERNO' | 'SN';
  motivoBypassFoto?: string;
  targetCuadrilla?: string;
  choferNombre?: string;
  placaVehiculo?: string;
  receptorNombre?: string;
  resumenTiendasClientes?: string;
  offlineNriFolio?: string;
  labelsSnapshot?: any[];
  batchMode?: 'SINGLE' | 'CHUNK' | 'FINAL';
  reservedNriFolio?: string | null;
  priorResults?: any[];
  priorReQuoteItems?: any[];
  priorHasFinancialBlock?: boolean;
  priorMaxOvercostPct?: number;
  supervisorAuth?: {
    supervisorId: string;
    supervisorName: string;
    supervisorPin?: string;
    authorizedAt?: string;
  };
}

export class ReceptionHttpError extends Error {
  status: number;
  isNetworkError: boolean;

  constructor(message: string, status: number, isNetworkError: boolean) {
    super(message);
    this.name = 'ReceptionHttpError';
    this.status = status;
    this.isNetworkError = isNetworkError;
  }
}

export interface ReceptionBatchProgress {
  currentBatch: number;
  totalBatches: number;
  processedItems: number;
  totalItems: number;
}

function getAuthHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = sessionStorage.getItem('sanesca_auth_jwt') || localStorage.getItem('sanesca_auth_jwt') || '';
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

export async function uploadEvidenceToR2(
  folioOAB: string,
  numeroNotaEntrega: string,
  imageBase64: string
): Promise<string> {
  const res = await fetch('/api/storage/upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({ folioOAB, numeroNotaEntrega, imageBase64 })
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `Error ${res.status} al subir evidencia a R2`);
  }

  const data = await res.json();
  return data.url;
}

export async function createOABSheet(payload: CreateOABPayload & { oabId?: string }) {
  const res = await fetch('/api/oab/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `Error ${res.status} al emitir OAB`);
  }

  return await res.json();
}

export async function saveOABDraft(payload: CreateOABPayload & { oabId?: string }) {
  const res = await fetch('/api/oab/draft', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `Error ${res.status} al guardar borrador OAB`);
  }

  return await res.json();
}

export async function fetchOABDrafts(): Promise<any[]> {
  try {
    const res = await fetch('/api/oab/details?list=drafts', {
      headers: { ...getAuthHeader() }
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.orders || [];
  } catch {
    return [];
  }
}

function isItemActiveForSaga(it: ReceptionItemPayload): boolean {
  const recHoy = Number(it.cantidadRecibidaHoy ?? it.cantidadRecibida ?? 0);
  const rejHoy = Number(it.cantidadRechazada ?? 0);
  if (recHoy > 0 || rejHoy > 0) return true;
  if (it.isSplitChild) return true;
  if (it.actionOnRemaining && it.actionOnRemaining !== 'BACKORDER') return true;
  if (it.motivoSaldo && it.motivoSaldo.trim().length > 0) return true;

  const hasOrigProj = it.originalProyectoNombre !== undefined && it.originalProyectoNombre !== null;
  if (hasOrigProj) {
    const norm = (val?: string) => {
      const s = String(val || '').trim().toLowerCase();
      if (!s || s.includes('stock general') || s.includes('stock fábrica') || s.includes('stock fabrica')) {
        return '__MTS__';
      }
      return s;
    };
    if (norm(it.originalProyectoNombre) !== norm(it.proyectoNombre)) return true;
  }
  return false;
}

async function postReceptionBatch(batchPayload: RegisterReceptionPayload) {
  let res: Response;
  try {
    res = await fetch('/api/oab/receive', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(batchPayload)
    });
  } catch (netErr: any) {
    throw new ReceptionHttpError(
      netErr?.message || 'Fallo de red al contactar el servidor de recepción',
      0,
      true
    );
  }

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const msg = errorData.error || `Error HTTP ${res.status} al registrar recepción`;
    throw new ReceptionHttpError(msg, res.status, false);
  }

  return await res.json();
}

export async function registerReception(
  payload: RegisterReceptionPayload,
  onProgress?: (info: ReceptionBatchProgress) => void
) {
  // Si el llamador especificó explícitamente un batchMode, enviar directo
  if (payload.batchMode) {
    return postReceptionBatch(payload);
  }

  const allItems = Array.isArray(payload.items) ? payload.items : [];
  const activeItems = allItems.filter(isItemActiveForSaga);
  const passiveItems = allItems.filter(it => !isItemActiveForSaga(it));

  const isSubOrder = Boolean(payload.parentFolioOAB && payload.parentFolioOAB !== payload.folioOAB);
  const maxActivePerBatch = isSubOrder ? 6 : 8;

  // PLAN-064 (P64-D1): Agrupar familias 1:N ("Dividir Destino") para que nunca se fragmenten en lotes distintos de la Saga
  const familyGroups = new Map<string, ReceptionItemPayload[]>();
  activeItems.forEach((it, idx) => {
    const rootKey = (it.parentSolicitudId || it.solicitudId || '').split(/_(?:split|child)_/)[0] || `_anon_${idx}`;
    const list = familyGroups.get(rootKey) || [];
    list.push(it);
    familyGroups.set(rootKey, list);
  });

  const activeChunks: ReceptionItemPayload[][] = [];
  let currentChunk: ReceptionItemPayload[] = [];
  for (const family of familyGroups.values()) {
    if (currentChunk.length > 0 && currentChunk.length + family.length > maxActivePerBatch) {
      activeChunks.push(currentChunk);
      currentChunk = [...family];
    } else {
      currentChunk.push(...family);
    }
  }
  if (currentChunk.length > 0) {
    activeChunks.push(currentChunk);
  }

  // Si cabe en un único lote (<= 8 activos, o 0 activos con puros pasivos), modo SINGLE
  if (activeChunks.length <= 1) {
    onProgress?.({
      currentBatch: 1,
      totalBatches: 1,
      processedItems: 0,
      totalItems: allItems.length
    });
    const singleRes = await postReceptionBatch({
      ...payload,
      batchMode: 'SINGLE'
    });
    onProgress?.({
      currentBatch: 1,
      totalBatches: 1,
      processedItems: allItems.length,
      totalItems: allItems.length
    });
    return singleRes;
  }

  // PLAN-055: Orquestador Multi-Lote Secuencial (Chunked Saga) anti-límite de 50 subrequests
  const totalBatches = activeChunks.length;
  let reservedNriFolio: string | null = payload.reservedNriFolio || null;
  const priorResults: any[] = [];
  let priorReQuoteItems: any[] = [];
  let priorHasFinancialBlock = false;
  let priorMaxOvercostPct = 0;
  let processedItemsCount = 0;

  for (let b = 0; b < totalBatches - 1; b++) {
    const chunkItems = activeChunks[b];
    onProgress?.({
      currentBatch: b + 1,
      totalBatches,
      processedItems: processedItemsCount,
      totalItems: allItems.length
    });

    const chunkRes = await postReceptionBatch({
      ...payload,
      items: chunkItems,
      batchMode: 'CHUNK',
      reservedNriFolio
    });

    if (chunkRes?.nriFolio) {
      reservedNriFolio = chunkRes.nriFolio;
    }
    if (Array.isArray(chunkRes?.detalle)) {
      priorResults.push(...chunkRes.detalle);
    }
    if (Array.isArray(chunkRes?.reQuoteItems)) {
      priorReQuoteItems = chunkRes.reQuoteItems;
    }
    if (chunkRes?.hasFinancialBlock) {
      priorHasFinancialBlock = true;
    }
    if ((chunkRes?.maxOvercostPct || 0) > priorMaxOvercostPct) {
      priorMaxOvercostPct = chunkRes.maxOvercostPct;
    }

    processedItemsCount += chunkItems.length;
  }

  // Lote Final ('FINAL'): Último grupo activo + todos los pasivos (0 subrequests c/u) + Cierre Cabecera + Snapshot NRI 360°
  const finalBatchItems = [...activeChunks[totalBatches - 1], ...passiveItems];
  onProgress?.({
    currentBatch: totalBatches,
    totalBatches,
    processedItems: processedItemsCount,
    totalItems: allItems.length
  });

  const finalRes = await postReceptionBatch({
    ...payload,
    items: finalBatchItems,
    batchMode: 'FINAL',
    reservedNriFolio,
    priorResults,
    priorReQuoteItems,
    priorHasFinancialBlock,
    priorMaxOvercostPct
  });

  onProgress?.({
    currentBatch: totalBatches,
    totalBatches,
    processedItems: allItems.length,
    totalItems: allItems.length
  });

  return finalRes;
}

export async function fetchNextFolioOAB(preview = true): Promise<string> {
  try {
    const res = await fetch(`/api/oab/next-folio?preview=${preview}`, {
      headers: { ...getAuthHeader() }
    });
    if (res.ok) {
      const data = await res.json();
      if (data.folio) {
        return data.folio;
      }
    }
  } catch (err) {
    console.warn('Fallo consultando endpoint /api/oab/next-folio, usando fallback determinista:', err);
  }
  return generateFolioOAB();
}

export function generateFolioOAB(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `OAB-${yyyy}${mm}${dd}-01`;
}

export interface OABReviewLine {
  solicitudId: string;
  nombre: string;
  codigo?: string;
  unidadMedida?: string;
  cantidadSolicitada: number;
  cantidadAprobada: number;
  isReviewed?: boolean;
  isRejected?: boolean;
  cantidadRecibidaPrevia?: number;
  backorderPendiente?: number;
  costoUnitarioUSD: number;
  subtotalUSD: number;
  proveedor?: string;
  proveedorNombre?: string;
  proveedorAdjudicado?: string;
  subOrdenFolio?: string | null;
  subOrdenId?: string | null;
  estadoFlujo?: string;
  dashboardId?: string;
  insumoId?: string;
  prioridad?: string;
  pedidoId?: string;
  proyectoId?: string;
  proyectoNombre?: string;
  tiendaNombre?: string;
  clienteNombre?: string;
  rutaLogistica?: 'VIA_GALPON' | 'DIRECTO_OBRA' | 'SERVICIO_SITIO' | 'NO_APLICA';
  orderLineId?: string | null;
  isDirectOrService?: boolean;
}

export interface OABReviewDetails {
  oab: {
    id: string;
    folio: string;
    fechaEmision: string;
    totalUSD: number;
    totalBs: number;
    tasaBCV: number;
    tasaBCVCotizacion?: number;
    tasaBCVLiquidacion?: number;
    isTasaFrozen?: boolean;
    estadoGeneral: string;
    nivelUrgenciaGlobal?: string;
    resumenTiendasClientes?: string;
    proveedor?: string;
    cotizacion?: string;
    fechaEstimadaEntrega?: string;
    notas?: string;
    notasCompras?: string;
    oabPadreId?: string | null;
    isMotherOAB?: boolean;
    isSubOrden?: boolean;
    subOrdenes?: {
      id: string;
      letter?: string;
      folio: string;
      proveedor: string;
      totalUSD: number;
      totalBs: number;
      estadoGeneral: string;
      numeroCotizacion?: string;
      fechaEstimadaEntrega?: string;
      comprobanteUrl?: string | null;
      lineCount?: number;
    }[];
  };
  lines: OABReviewLine[];
  pendingLines?: OABReviewLine[];
  completedLines?: OABReviewLine[];
  canceledLines?: OABReviewLine[];
}

export interface SubmitReviewPayload {
  action?: 'SAVE_QUOTES' | 'APPROVE_PURCHASE';
  oabId: string;
  folioOAB: string;
  tasaBCV?: number;
  proveedorNombre?: string;
  numeroCotizacion?: string;
  fechaEstimadaEntrega?: string;
  notasCompras?: string;
  motivoDelegacion?: string;
  comprobanteUrl?: string;
  lineas: {
    solicitudId: string;
    dashboardId?: string;
    insumoId?: string;
    nombre?: string;
    proveedor?: string;
    proveedorNombre?: string;
    pedidoId?: string;
    proyectoId?: string;
    proyectoNombre?: string;
    cantidadSolicitada?: number;
    cantidadAprobada: number;
    costoUnitarioUSD: number;
    rutaLogistica?: string;
    orderLineId?: string | null;
    isExcessMtsSplit?: boolean;
    parentSolicitudId?: string;
  }[];
}

export interface CancelOABPayload {
  folioOAB: string;
  oabId?: string;
  motivoCancelacion: string;
}

export async function cancelOAB(payload: CancelOABPayload) {
  const res = await fetch('/api/oab/cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `Error ${res.status} al anular la OAB`);
  }

  return await res.json();
}

export async function fetchPendingOABs(status: string = 'all'): Promise<any[]> {
  try {
    const res = await fetch(`/api/oab/details?list=${encodeURIComponent(status)}`, {
      headers: { ...getAuthHeader() }
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.orders || [];
  } catch {
    return [];
  }
}

export async function fetchOABDetails(folio: string): Promise<OABReviewDetails | null> {
  const res = await fetch(`/api/oab/details?folio=${encodeURIComponent(folio)}`, {
    headers: { ...getAuthHeader() }
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'No se pudo cargar la orden');
  }
  return await res.json();
}

export async function submitOABReview(payload: SubmitReviewPayload) {
  const res = await fetch('/api/oab/review', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Error al guardar la revisión de OAB');
  }

  return await res.json();
}

export async function fetchRampReceptions(params?: {
  folioOAB?: string;
  estado?: string;
  q?: string;
  refresh?: boolean;
}): Promise<any[]> {
  try {
    const qs = new URLSearchParams();
    if (params?.folioOAB) qs.set('folioOAB', params.folioOAB);
    if (params?.estado) qs.set('estado', params.estado);
    if (params?.q) qs.set('q', params.q);
    if (params?.refresh) qs.set('refresh', 'true');
    const url = `/api/oab/receptions${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await fetch(url, { headers: { ...getAuthHeader() } });
    if (!res.ok) return [];
    const data = await res.json();
    return data.receptions || [];
  } catch {
    return [];
  }
}

export async function fetchRampReceptionByNri(nriFolio: string): Promise<any | null> {
  try {
    const res = await fetch(`/api/oab/receptions?nri=${encodeURIComponent(nriFolio)}`, {
      headers: { ...getAuthHeader() }
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.reception || null;
  } catch {
    return null;
  }
}


